import { inRange } from 'lodash/fp';

import { OrderbookSide, PriceLevel } from '@exchange/libs/orderbook/service/src';
import { themingInstance } from '@exchange/libs/utils/theming/src';

// Dimensions
export const RENDERING_SCALE = 2;
export const PADDING_HORIZONTAL = 10 * RENDERING_SCALE;

export const NUMBER_OF_PRICELEVELS = 50;

const getColor = themingInstance.getColor.bind(themingInstance);

export function getItemHeight() {
  return 25 * RENDERING_SCALE;
}

export function getCanvasHeight(itemHeight: number) {
  return itemHeight * NUMBER_OF_PRICELEVELS;
}

export function getPaddingVertical(itemHeight: number) {
  return itemHeight * 0.7;
}

export function accentColor(pl: PriceLevel) {
  return pl.side === OrderbookSide.ASK ? getColor('--v-theme-error') : getColor('--v-theme-success');
}

export function accentBackgroundColor(pl: PriceLevel) {
  return pl.side === OrderbookSide.ASK ? getColor('--error-darkest') : getColor('--success-darkest');
}

export function getTotalColor() {
  return getColor('--v-theme-text-primary');
}

export function getAmountColor() {
  return getColor('--v-theme-text-primary');
}

export function getPriceColor(pl: PriceLevel) {
  return accentColor(pl);
}

export function getAmountBarColor(pl: PriceLevel) {
  return accentBackgroundColor(pl);
}

export function getBackgroundColor() {
  return getColor('--v-theme-elevation-1');
}

export function getY(side: OrderbookSide, index: number, itemHeight: number, canvasHeight: number) {
  return side === OrderbookSide.ASK ? canvasHeight - itemHeight * (index + 1) : itemHeight * index;
}

export function getColumnWidth(canvasWidth: number, columnsNumber: number) {
  return (canvasWidth - PADDING_HORIZONTAL * 2) / columnsNumber;
}

export enum LevelColumns {
  total = 'total',
  amount = 'amount',
  price = 'price',
}

export const getAppliedPrecisionString = (value: number, precision: number) => value.toFixed(precision);

type OrderbookPriceLevelColumnPrecision = {
  marketPrecision: number;
  amountPrecision: number;
  totalPrecision: number;
};

export type OrderbookPriceLevelColumn = {
  prop: LevelColumns;
  getColor: (pl: PriceLevel) => string;
  value: (pl: PriceLevel, precisions: OrderbookPriceLevelColumnPrecision) => string;
  lKey: string;
};
export const getColumns = (miniVersion = false): Array<OrderbookPriceLevelColumn> => {
  const total = {
    prop: LevelColumns.total,
    getColor: () => getTotalColor(),
    value: (pl: PriceLevel, precisions: OrderbookPriceLevelColumnPrecision) => getAppliedPrecisionString(pl.total, precisions.totalPrecision),
    lKey: 'modules.orderbook.total',
  };
  const amount = {
    prop: LevelColumns.amount,
    getColor: () => getAmountColor(),
    value: (pl: PriceLevel, precisions: OrderbookPriceLevelColumnPrecision) => getAppliedPrecisionString(pl.amount, precisions.amountPrecision),
    lKey: 'modules.orderbook.size',
  };
  const price = {
    prop: LevelColumns.price,
    getColor: (pl: PriceLevel) => getPriceColor(pl),
    value: (pl: PriceLevel, precisions: OrderbookPriceLevelColumnPrecision) => getAppliedPrecisionString(pl.price, precisions.marketPrecision),
    lKey: 'modules.orderbook.price',
  };

  if (miniVersion) {
    return [amount, price];
  }

  return [total, amount, price];
};

export const getClickedPricelevelProp = (event: MouseEvent, width: number, mini: boolean) => {
  const rect = (event.currentTarget as HTMLElement | null)?.getBoundingClientRect();

  const pointerX = event.clientX - (rect?.left || 0);
  const offsetWidth = rect?.width || width;
  const pointerXPercent = Math.round((pointerX / offsetWidth) * 100);

  if (!mini) {
    // three columns between ranges 0/33, 33/66, 66/100
    if (inRange(33, 66, pointerXPercent)) {
      return LevelColumns.amount;
    }

    if (inRange(66, 100, pointerXPercent)) {
      return LevelColumns.price;
    }

    return LevelColumns.total;
  }

  if (inRange(0, 48, pointerXPercent)) {
    // amount column usually look a bit smaller
    return LevelColumns.amount;
  }

  return LevelColumns.price;
};

export interface ColumnAlignmentParams {
  column: OrderbookPriceLevelColumn;
  mini: boolean;
}

export type ColumnTextAlignment = 'left' | 'right';

/**
 * Returns the text alignment for an orderbook column based on given input.
 *
 * @param column Column to get alignment for
 * @param mini Get alignment for mini or regular version
 */
export const getColumnTextAlignment = ({ column, mini }: ColumnAlignmentParams): ColumnTextAlignment => {
  if ((mini && column.prop === LevelColumns.amount) || column.prop === LevelColumns.total) {
    return 'left';
  }

  return 'right';
};

export interface DisplayedValue {
  text: number;
  color: string;
}
